import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Database, MessageSquare, Zap, ArrowRight } from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: Shield,
      title: "Sovereign AI Infrastructure",
      headline: "Beyond the Public Cloud: Private LLM Orchestration",
      description: "Deploy state-of-the-art LLMs exclusively within your VPC with zero data retention.",
      path: "/services/sovereign-ai",
      gradient: "from-blue-600 to-purple-600",
      features: ["Zero Data Retention", "VPC Deployment", "Kubernetes Auto-scaling", "Enterprise Security"]
    },
    {
      icon: Database,
      title: "Financial Semantic Lakehouse",
      headline: "Turning Dark Data into Liquid Assets",
      description: "AI-native data lakes that transform unstructured financial documents into actionable insights.",
      path: "/services/semantic-lakehouse",
      gradient: "from-green-600 to-teal-600",
      features: ["Recursive Text Splitting", "Semantic Chunking", "Vector Engine", "ETL Intelligence"]
    },
    {
      icon: MessageSquare,
      title: "Precision-RAG",
      headline: "Eliminating Hallucinations through Hybrid Search",
      description: "Every response backed by deterministic citations with complete audit trails.",
      path: "/services/precision-rag",
      gradient: "from-orange-600 to-red-600",
      features: ["Hybrid Search", "Deterministic Citations", "Real-time Verification", "Audit Trail"]
    },
    {
      icon: Zap,
      title: "Agentic Dashboard Generator",
      headline: "Prompt-to-Insight: Future of Internal Reporting",
      description: "Generate interactive dashboards instantly using natural language queries.",
      path: "/services/agentic-dashboard",
      gradient: "from-purple-600 to-pink-600",
      features: ["Natural Language Queries", "React Generation", "AI Analysis", "Real-time Integration"]
    }
  ];

  return (
    <div className="pt-16 min-h-screen bg-navy-950 text-white">
      <div className="container mx-auto px-6 lg:px-12 py-12">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-6">
            <span className="text-gradient-blue">
              Enterprise AI Solutions
            </span>
          </h1>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto">
            World-class AI infrastructure designed specifically for financial institutions. 
            Click on any service to explore detailed capabilities and implementation.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <Link
                key={index}
                to={service.path}
                className="group relative bg-navy-800 rounded-2xl p-8 border border-slate-700/40 premium-border premium-elevate transition-all duration-300 hover:border-orange-500/50 hover:shadow-2xl hover:shadow-orange-500/10"
              >
                <div className={`absolute inset-0 bg-gradient-to-r ${service.gradient} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-300`}></div>
                <div className="relative z-10">
                  <div className={`inline-flex p-4 rounded-lg bg-gradient-to-r ${service.gradient} mb-6`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3 text-white">{service.title}</h3>
                  <h4 className="text-lg font-semibold text-orange-400 mb-4">{service.headline}</h4>
                  <p className="text-slate-300 leading-relaxed mb-6">{service.description}</p>
                  
                  <div className="mb-6">
                    <h5 className="text-sm font-semibold text-slate-400 mb-3">Key Features:</h5>
                    <div className="grid grid-cols-2 gap-2">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <div className="w-1.5 h-1.5 bg-orange-400 rounded-full"></div>
                          <span className="text-sm text-slate-300">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex items-center text-orange-400 group-hover:text-orange-300 transition-colors">
                    <span className="font-medium">Explore Service</span>
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="text-center bg-gradient-to-r from-orange-900/15 to-amber-900/15 rounded-2xl p-12 border border-slate-700/40 premium-border premium-elevate">
          <h2 className="text-3xl font-bold mb-4 text-white">Ready to Transform Your AI Infrastructure?</h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Schedule a consultation with our enterprise solutions team to discuss your specific requirements and see how BeeMind AI can accelerate your AI transformation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/#demo"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#f97316] to-[#fbce16] text-white font-semibold rounded-lg hover:from-[#fb7a1f] hover:to-[#fbd32b] transition-all duration-300 transform hover:scale-105"
            >
              Schedule Consultation
            </Link>
            <Link
              to="/"
              className="inline-flex items-center px-8 py-4 border border-slate-600/50 text-white font-semibold rounded-lg hover:bg-white/5 transition-all duration-300"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Services;
